import { Box3, Frustum, Raycaster, Vector3 } from 'three';

export interface OctNodeI {
  boundingBox: Box3;
  data?: any;
  children: OctNodeI[];
}

export class OctTree {
  private treeDepth: number;

  constructor(public rootNode: OctNodeI) {
    this.treeDepth = 0;
    const findDepth = (node: OctNodeI, curDepth: number) => {
      this.treeDepth = Math.max(this.treeDepth, curDepth);

      node.children.map((child) => findDepth(child, curDepth + 1));
    };

    findDepth(rootNode, 1);
  }

  filter(
    frustum: Frustum,
    LODDistances: number[],
    cameraPosition: Vector3,
    maxDepth = 999,
    maxNodeSize = -1,
  ): OctNodeI[] {
    const nodes: OctNodeI[] = [];

    const parseTree = (node: OctNodeI, curDepth: number) => {
      let addChildren = false;
      const childDepthAllowed = maxDepth < curDepth + 1;
      if (childDepthAllowed) {
        node.children.map((child) => {
          if (
            evalIncludeOctNode(
              child.boundingBox,
              frustum,
              cameraPosition,
              LODDistances[curDepth],
              maxNodeSize,
            )
          ) {
            addChildren = true;
          }
        });
      }

      if (!addChildren) {
        nodes.push(node);
      } else {
        node.children.map((child) => {
          parseTree(child, curDepth + 1);
        });
      }
    };

    parseTree(this.rootNode, 0);
    return nodes;
  }

  getDepth(): number {
    return this.treeDepth;
  }

  pickTree(raycaster: Raycaster): { distance: number; node: OctNodeI }[] {
    const intersecetedLeafNodes: { distance: number; node: OctNodeI }[] = [];
    const pickNode = (node: OctNodeI) => {
      let goDeeper = false;
      node.children.map((child) => {
        const childIntersections = raycaster.ray.intersectBox(child.boundingBox, new Vector3());
        if (childIntersections !== null) {
          goDeeper = true;
        }
      });

      const nodeIntersection = raycaster.ray.intersectBox(node.boundingBox, new Vector3());
      if (nodeIntersection !== null) {
        intersecetedLeafNodes.push({
          distance: nodeIntersection.distanceTo(raycaster.ray.origin),
          node,
        });
      }

      if (goDeeper) {
        node.children.map((child) => {
          pickNode(child);
        });
      }
    };

    pickNode(this.rootNode);
    return intersecetedLeafNodes;
  }

  getSize(): Vector3 {
    return this.rootNode.boundingBox.getSize(new Vector3());
  }
}

const evalIncludeOctNode = (
  boundingBox: Box3,
  frustum: Frustum,
  cameraPosition: Vector3,
  maxDepthDistance: number,
  maxNodeSize: number,
): boolean => {
  const nodeBounderies: Vector3 = new Vector3();
  boundingBox.getSize(nodeBounderies);

  return (
    frustum.intersectsBox(boundingBox) &&
    boundingBox.distanceToPoint(cameraPosition) < maxDepthDistance &&
    nodeBounderies.x > maxNodeSize &&
    nodeBounderies.y > maxNodeSize
  );
};
